<template>
  <div class="job-architecture-page">
    <div v-if="familyNotFound" class="job-architecture-not-found">
      <div class="job-architecture-header">
        <h2 class="text-h1">
          Job Family Profiles
        </h2>
        <p class="text-body-2 text-medium-emphasis">
          Compare job profiles in this family side by side.
        </p>
      </div>
      <div class="pa-4">
        <p class="text-medium-emphasis">
          Family not found.
        </p>
        <router-link :to="{ name: 'job-architecture' }">
          Back to Job Architecture
        </router-link>
      </div>
    </div>

    <template v-else>
      <div class="job-architecture-breadcrumb-row">
        <router-link
          :to="{ name: 'job-architecture' }"
          class="breadcrumb-back-link text-body-2"
        >
          Job Architecture
        </router-link>
        <span class="breadcrumb-separator text-body-2">/</span>
        <JobArchitectureFamilyBreadcrumb />
      </div>

      <div class="job-architecture-header">
        <h2 class="text-h1">
          Job Family Profiles
        </h2>
        <p class="text-body-2 text-medium-emphasis">
          Compare job profiles in this family side by side.
        </p>
      </div>

      <div class="job-architecture-controls-row">
        <div class="job-architecture-filters-left">
          <ColumnsFilterDropdown
            v-if="tableControls"
            :columns="tableControls.profileColumns"
            :visibility="tableControls.columnVisibilityState"
            @toggle-column="tableControls.toggleColumnVisibility"
            @select-all="tableControls.setAllColumnsVisible"
          />
          <RowsFilterDropdown
            v-if="tableControls"
            :fields="tableControls.orderedFieldsForRows || tableControls.allFields"
            :visible-ids="tableControls.visibleFieldIdsArray"
            @toggle-field="tableControls.toggleFieldVisibility"
            @select-all="tableControls.setAllRowsVisible"
          />
        </div>
        <div class="job-architecture-filters-right">
          <v-btn
            variant="outlined"
            rounded="lg"
            class="filter-style-btn"
            density="comfortable"
            :disabled="!tableControls"
            @click="tableControls?.downloadCsv?.({ fileName: csvDownloadFileName })"
          >
            <v-icon icon="mdi-download" start />
            Download CSV
          </v-btn>
        </div>
      </div>

      <JobProfileComparisonTable
        v-if="selectedFamilyProfiles.length > 0"
        :profiles="selectedFamilyProfiles"
        @update:controls="tableControls = $event"
      />
      <p
        v-else-if="profilesLoading"
        class="text-medium-emphasis pa-4"
      >
        Loading profiles…
      </p>
      <p
        v-else
        class="text-medium-emphasis pa-4"
      >
        This job family has no job profiles.
      </p>
    </template>
  </div>
</template>

<script setup>
import { format } from 'date-fns';
import {
  ref, computed, watch, onMounted,
} from 'vue';
import { useRoute } from 'vue-router';
import { storeToRefs } from 'pinia';
import { useJobArchitecture } from '@/stores/jobArchitecture/jobArchitecture';
import { useAppLoading } from '@/stores/app/loading';
import { findFamilyByUlid } from '@/components/jobArchitecture/helpers';
import JobArchitectureFamilyBreadcrumb from '@/components/jobArchitecture/JobArchitectureFamilyBreadcrumb.vue';
import JobProfileComparisonTable from '@/components/jobArchitecture/JobProfileComparisonTable.vue';
import ColumnsFilterDropdown from '@/components/jobArchitecture/ColumnsFilterDropdown.vue';
import RowsFilterDropdown from '@/components/jobArchitecture/RowsFilterDropdown.vue';

const route = useRoute();

const { treeData, selectedFamilyProfiles } = storeToRefs(useJobArchitecture());
const { listLoading: profilesLoading } = storeToRefs(useAppLoading());

const { loadTree, selectFamily } = useJobArchitecture();

const tableControls = ref(null);
const familyNotFound = ref(false);

const csvDownloadFileName = computed(() => `job-family-profiles-${format(new Date(), 'MM-dd-yyyy')}.csv`);

const loadFamilyFromRoute = async () => {
  const { familyUlid } = route.params;

  if (!familyUlid) {
    familyNotFound.value = true;

    return;
  }

  await loadTree();

  const family = findFamilyByUlid(treeData.value, familyUlid);

  if (!family) {
    familyNotFound.value = true;

    return;
  }

  familyNotFound.value = false;

  await selectFamily(family);
};

onMounted(() => {
  loadFamilyFromRoute();
});

watch(
  () => route.params.familyUlid,
  () => {
    loadFamilyFromRoute();
  },
);
</script>

<style scoped lang="less">
.job-architecture-page {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 1400px;
  margin-left: auto;
  margin-right: auto;
  padding: 24px 24px 16px;
}

.job-architecture-not-found .job-architecture-header {
  padding-top: 8px;
}

.job-architecture-breadcrumb-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.job-architecture-header {
  margin-bottom: 24px;
}

.job-architecture-controls-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 12px;
  flex-wrap: wrap;
}

.breadcrumb-back-link {
  color: @text-brand;
  text-decoration: none;
}

.breadcrumb-back-link:hover {
  text-decoration: underline;
}

.breadcrumb-separator {
  color: @grey-500;
}

.job-architecture-filters-left,
.job-architecture-filters-right {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  align-items: center;
}

.filter-style-btn {
  background-color: @grey-100 !important;
  border: 1px solid @stroke-default;
  color: @text-darkest;
  font-weight: 400;
}
</style>
